/**
 * What may leave the device in diagnostics and analytics (M11, the mobile
 * security pass; applies to the website too, which runs the same code).
 *
 * Three sinks get data the user never chose to send: error_logs (every
 * logError call), Sentry (when a DSN is configured) and OpenPanel (with
 * analytics consent). None of them needs a person's address, phone number,
 * email, free text or exact location to do its job, and none needs a URL's
 * query string or fragment — which is where auth codes, reset tokens and
 * filter values (`?phone=eq.…`) travel. So:
 *
 *   scrubMetadata  replaces the value of any key that names personal data,
 *                  at any depth, and cuts off deep or circular structures;
 *   stripUrl       keeps scheme, host and path, drops ?query and #fragment;
 *   scrubText      for free text that is sent anyway (an error's message
 *                  and stack): emails, phone-like digit runs and URL
 *                  queries/fragments inside it are replaced;
 *   scrubAnalyticsEvent  applies stripUrl to the URL-bearing properties of
 *                  an OpenPanel event in place (its `filter` hook is the
 *                  one point every event passes through before sending);
 *                  an outgoing link keeps only its origin, or its scheme
 *                  (tel:, mailto:, sms:), and loses its visible text;
 *                  every property named as an id is dropped and every
 *                  amount becomes a power-of-ten band (M11 P2).
 *
 * Over-redaction is the accepted failure mode: a diagnostic that lost a
 * field can be reproduced; a phone number in a log cannot be taken back.
 */

const REDACTED = '[redacted]';
const MAX_DEPTH = 5;

// Key names, case-insensitive, anywhere in the key: camelCase and snake_case
// both match (clientAddress, client_address, mobileMoneyPhone, …).
const SENSITIVE_KEY =
  /address|phone|msisdn|e-?mail|instruction|note|message|comment|description|password|passcode|secret|token|otp|pin_?code|national_?id|id_?number|document_?number|lat$|lng$|lon$|latitude|longitude|coords?$|geo|first_?name|last_?name|full_?name|^name$|search_?query|^query$|^q$/i;

// Places (M11 QA pass): only as a whole last word of the key, because as
// substrings they hide inside operational names ("capacity", "allocation").
// Case-sensitive on purpose so camelCase can be told apart: `city`,
// `user_city` and `deliveryCity` match; `capacity` does not.
const PLACE_KEY =
  /(?:^|_)(?:location|city|town|district|village|street|landmark|postcode|neighbou?rhood|area)$|[a-z0-9](?:Location|City|Town|District|Village|Street|Landmark|Postcode|Neighbou?rhood|Area)$/;

export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY.test(key) || PLACE_KEY.test(key) || PLACE_KEY.test(key.toLowerCase());
}

/** Scheme, host and path only. Relative URLs keep their path. */
export function stripUrl(value: string): string {
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
}

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// 9+ digits, allowing a leading + and single spaces, dots or dashes between
// groups: phone numbers and mobile money wallets in any of the formats the
// app accepts. A UUID is matched first and kept: an all-digit one would
// otherwise read as a number.
const UUID_OR_PHONE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b|(\+?\(?\d(?:[\s.()-]{0,2}\d){8,})/gi;
// Postgres puts the offending values in the message: "Key (phone)=(0772…)"
// and "Failing row contains (…, Jane, 12 Acacia Avenue, …)".
const PG_KEY_VALUES = /(Key \([^)]*\)=)\((?:[^()]|\([^()]*\))*\)/g;
const PG_FAILING_ROW = /(Failing row contains )\((?:[^()]|\([^()]*\))*\)/g;
// A one-time code, PIN or password next to the word that names it. The value
// must hold a digit ("password failed" is prose), and a bare "code" is not a
// label: SQLSTATE and HTTP codes are diagnostics.
const CODE_NEAR_LABEL = /\b(otp|pin|passcode|password|token|secret|verification code)(\W{1,3})((?=[^\s,;]*\d)[^\s,;]{4,})/gi;
// Any URL-ish token — absolute, protocol-relative (//host/…) or a path
// (/reset-password) — that has a query or fragment: keep the part before it.
// A token needs a "/" before the "?" to count, so prose questions survive.
const URL_QUERY = /(\/[^\s?#'"`]*)[?#][^\s'"`)]*/g;

/** Free text with emails, phone-like numbers and URL queries replaced. */
export function scrubText(value: string): string {
  return value
    .replace(PG_KEY_VALUES, '$1([redacted])')
    .replace(PG_FAILING_ROW, '$1([redacted])')
    .replace(URL_QUERY, '$1')
    .replace(EMAIL, '[email]')
    .replace(CODE_NEAR_LABEL, '$1$2[redacted]')
    .replace(UUID_OR_PHONE, (m, phone) => (phone ? '[number]' : m));
}

/**
 * An outgoing link reduced to where it goes, not what it carries: an http(s)
 * URL keeps its origin (a wa.me/<phone> path is a phone number), any other
 * scheme keeps only the scheme (tel:, mailto:, sms:, whatsapp:).
 */
export function linkDestination(href: string): string {
  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.origin : url.protocol;
  } catch {
    return '[link]';
  }
}

export function scrubMetadata(value: unknown, depth = 0, seen: WeakSet<object> = new WeakSet()): unknown {
  // Strings under ordinary keys are free text too (a cause, a filename URL).
  if (typeof value === 'string') return scrubText(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return '[truncated]';
  if (seen.has(value)) return '[circular]';
  seen.add(value);
  if (Array.isArray(value)) return value.map((v) => scrubMetadata(v, depth + 1, seen));
  if (value instanceof Error) return { name: value.name, message: scrubText(value.message) };
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = isSensitiveKey(k) ? REDACTED : scrubMetadata(v, depth + 1, seen);
  }
  return out;
}

/** Same as scrubMetadata, typed for the record-shaped call sites. */
export function scrubRecord(value: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  return value === undefined ? undefined : (scrubMetadata(value) as Record<string, unknown>);
}

const URL_PROPERTIES = ['__path', '__referrer'] as const;

// Analytics ids and amounts (M11 P2). A booking, helper, client or user id
// joins an event to a row in the database, and an exact amount nearly does;
// OpenPanel is self-hosted and the ids are pseudonymous, so this is
// hardening. Ids are DROPPED rather than hashed: a stable hash is still a
// join key for anyone holding the table, a per-install salted one needs an
// async digest the SDK's synchronous filter cannot wait for, and no report
// counts distinct bookings or helpers from properties (the funnel joins on
// OpenPanel's own device and session ids). Amounts become a power-of-ten
// band, which still splits small jobs from large ones in any currency.
//   id keys: `id`, `*_id`, `*Id`, their plurals, anything with `uuid`;
//   a boolean under one (`has_national_id`) is a flag, and is kept.
//   amount keys: amount, price, fee, cost, revenue, payout, earning(s),
//   balance, tip, commission, refund, subtotal and total, as the whole key or
//   its last word (`total_amount`, `helperPayout`), so `total_steps` stays.
const ID_KEY = /^ids?$|_ids?$|uuid/i;
const ID_KEY_CAMEL = /[a-z0-9]Ids?$/;
const AMOUNT_WORDS = ['amount', 'price', 'fee', 'fees', 'cost', 'revenue', 'payout', 'earning', 'earnings', 'balance', 'tip', 'commission', 'refund', 'subtotal', 'total'];
const AMOUNT_KEY = new RegExp(`(?:^|_)(?:${AMOUNT_WORDS.join('|')})$`, 'i');
const AMOUNT_KEY_CAMEL = new RegExp(`[a-z0-9](?:${AMOUNT_WORDS.map((w) => w[0].toUpperCase() + w.slice(1)).join('|')})$`);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_IN_PATH = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;

export function isAnalyticsIdKey(key: string): boolean {
  return ID_KEY.test(key) || ID_KEY_CAMEL.test(key);
}

export function isAnalyticsAmountKey(key: string): boolean {
  return AMOUNT_KEY.test(key) || AMOUNT_KEY_CAMEL.test(key);
}

const BAND_LABELS = ['1', '10', '100', '1k', '10k', '100k', '1M', '10M', '100M', '1B'];

/**
 * An amount as the power-of-ten band it falls in ("10k-100k"), in whatever
 * currency it was: 45,000 → "10k-100k". Zero stays "0", a negative amount
 * keeps its sign, and anything that is not a finite number is null.
 */
export function amountBand(value: unknown): string | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return null;
  if (n === 0) return '0';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs < 1) return `${sign}<1`;
  const exp = Math.floor(Math.log10(abs));
  if (exp >= BAND_LABELS.length - 1) return `${sign}${BAND_LABELS[BAND_LABELS.length - 1]}+`;
  return `${sign}${BAND_LABELS[exp]}-${BAND_LABELS[exp + 1]}`;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;
}

/**
 * Ids out, amounts banded, in place and at any depth (bounded). The SDK's
 * own `__` properties are left to the URL rules above.
 */
function scrubAnalyticsValues(props: Record<string, unknown>, depth: number): void {
  for (const key of Object.keys(props)) {
    if (key.startsWith('__')) continue;
    const v = props[key];
    if (isAnalyticsIdKey(key)) {
      // A boolean is a flag about an id (`has_national_id`), never the id
      // itself, so it stays -- as `has_tip` does among the amounts.
      if (typeof v !== 'boolean') delete props[key];
    } else if (isAnalyticsAmountKey(key)) {
      // A flag such as `has_tip` stays a flag; a number becomes its band.
      if (typeof v !== 'boolean' && v !== null && v !== undefined) props[key] = amountBand(v);
    } else if (typeof v === 'string' && UUID.test(v.trim())) {
      // An id under a name the rules above do not know (`helper`, `ref`).
      delete props[key];
    } else if (Array.isArray(v)) {
      const kept = v.filter((x) => !(typeof x === 'string' && UUID.test(x.trim())));
      if (depth < MAX_DEPTH) for (const x of kept) if (isPlainObject(x)) scrubAnalyticsValues(x, depth + 1);
      props[key] = kept;
    } else if (isPlainObject(v)) {
      if (depth < MAX_DEPTH) scrubAnalyticsValues(v, depth + 1);
      else delete props[key];
    }
  }
}

/**
 * OpenPanel's `filter` receives each outgoing event by reference before it
 * is sent; this rewrites its properties in place. Every event passes here —
 * trackAnalyticsEvent's, screen views, identify traits, and the SDK's own
 * link_out and data-track events — so no caller can send what it drops.
 * Always returns true — consent is decided separately.
 *
 *   - URLs (__path, __referrer) lose their query and fragment, and a UUID
 *     path segment becomes `:id` (/helper/<uuid> → /helper/:id).
 *   - Ids are dropped and amounts banded (see ID_KEY and AMOUNT_WORDS).
 */
export function scrubAnalyticsEvent(event: unknown): true {
  const payload = (event as { payload?: { properties?: Record<string, unknown> } } | null)?.payload;
  const props = payload?.properties;
  if (props && typeof props === 'object') {
    for (const key of URL_PROPERTIES) {
      const v = props[key];
      if (typeof v === 'string') props[key] = stripUrl(v).replace(UUID_IN_PATH, '/:id');
    }
    // link_out: the SDK sends the link's href and its visible text.
    if (typeof props.href === 'string') props.href = linkDestination(props.href);
    if ('text' in props) delete props.text;
    // screen_view: the SDK adds document.title, which names the person on a
    // profile page ("Jane D. | Kanfy"). The path already says which page.
    if ('__title' in props) delete props.__title;
    scrubAnalyticsValues(props, 0);
  }
  return true;
}
